import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { logout, fetchManagers, saveManagersToSupabase, fetchArticles, saveArticlesToSupabase, type NewsArticle } from '@/utils/adminStore';
import NewsManagement from './NewsManagement';
import SiteConfigManagement from './SiteConfigManagement';
import FoundingTeamManagement from './FoundingTeamManagement';
import CoreManagementTeamManagement from './CoreManagementTeamManagement';

interface Props {
  onLogout: () => void;
}

type Tab = 'founding' | 'coreManagement' | 'news' | 'config';

interface ExportData {
  version: number;
  exportedAt: string;
  managers: Awaited<ReturnType<typeof fetchManagers>>;
  articles: NewsArticle[];
}

export default function Dashboard({ onLogout }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>('config');
  const [refreshKey, setRefreshKey] = useState(0);
  const [importStatus, setImportStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [importMessage, setImportMessage] = useState('');
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLogout = () => {
    logout();
    onLogout();
  };

  const handleExportExcel = async () => {
    setExportMenuOpen(false);
    const managers = await fetchManagers();
    const articles = await fetchArticles();

    const wb = XLSX.utils.book_new();

    // Sheet 1: 管理团队
    const managerRows = managers.map((m) => ({
      ID: m.id,
      '姓名': m.name,
      '职位': m.position,
      '城市': m.city || '',
      '从业经验': m.experience || '',
      '服务理念': m.philosophy || '',
      '排序': m.sort_order,
    }));
    const ws1 = XLSX.utils.json_to_sheet(managerRows);
    // Set column widths
    ws1['!cols'] = [
      { wch: 6 },
      { wch: 12 },
      { wch: 20 },
      { wch: 10 },
      { wch: 16 },
      { wch: 36 },
      { wch: 6 },
    ];
    XLSX.utils.book_append_sheet(wb, ws1, '管理团队');

    // Sheet 2: 资讯文章
    const articleRows = articles.map((a) => ({
      ID: a.id,
      '标题': a.title,
      '日期': a.date,
      '分类': a.category || '',
      '摘要': a.summary || '',
    }));
    const ws2 = XLSX.utils.json_to_sheet(articleRows);
    ws2['!cols'] = [
      { wch: 6 },
      { wch: 40 },
      { wch: 12 },
      { wch: 10 },
      { wch: 60 },
    ];
    XLSX.utils.book_append_sheet(wb, ws2, '资讯文章');

    XLSX.writeFile(wb, `数据备份-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleExportJSON = async () => {
    setExportMenuOpen(false);
    const managers = await fetchManagers();
    const articles = await fetchArticles();
    const data: ExportData = {
      version: 2,
      exportedAt: new Date().toISOString(),
      managers,
      articles,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `数据备份-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportStatus('idle');
    setImportMessage('');

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const raw = event.target?.result;
        if (typeof raw !== 'string') {
          setImportStatus('error');
          setImportMessage('文件读取失败');
          return;
        }

        const data = JSON.parse(raw) as ExportData;

        if (!data.version || !Array.isArray(data.managers) || !Array.isArray(data.articles)) {
          setImportStatus('error');
          setImportMessage('文件格式不正确，请选择正确的备份文件');
          return;
        }

        if (data.version >= 2) {
          const managersOk = data.managers.every(
            (m: any) => typeof m.name === 'string' && typeof m.position === 'string'
          );
          if (!managersOk) {
            setImportStatus('error');
            setImportMessage('成员数据格式校验失败');
            return;
          }
          const ok = await saveManagersToSupabase(data.managers);
          if (!ok) {
            setImportStatus('error');
            setImportMessage('成员数据导入到云端失败');
            return;
          }
        } else {
          const managersOk = data.managers.every(
            (m: any) => typeof m.position === 'string' && typeof m.name === 'string'
          );
          if (!managersOk) {
            setImportStatus('error');
            setImportMessage('成员数据格式校验失败');
            return;
          }
          const legacyManagers = data.managers.map((m: any, i: number) => ({
            id: 0,
            name: m.name,
            position: m.position,
            city: m.city || '',
            store: m.store || '',
            experience: m.experience || '',
            philosophy: m.philosophy || '',
            image_prompt: m.imagePrompt || '',
            image_url: m.imageUrl || '',
            sort_order: i,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }));
          const ok = await saveManagersToSupabase(legacyManagers);
          if (!ok) {
            setImportStatus('error');
            setImportMessage('成员数据导入到云端失败');
            return;
          }
        }

        const articlesOk = data.articles.every(
          (a: any) => typeof a.id === 'number' && typeof a.title === 'string'
        );

        if (articlesOk) {
          await saveArticlesToSupabase(data.articles);
        }

        const managerCount = data.managers.length;
        const articleMsg = articlesOk ? `，资讯 ${data.articles.length} 篇` : '';
        setImportStatus('success');
        setImportMessage(`导入成功！成员 ${managerCount} 位${articleMsg}`);
        setRefreshKey((k) => k + 1);
      } catch {
        setImportStatus('error');
        setImportMessage('文件解析失败，请确认是有效的 JSON 文件');
      }
    };

    reader.onerror = () => {
      setImportStatus('error');
      setImportMessage('文件读取失败');
    };

    reader.readAsText(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-brand-gray">
      <header className="bg-white border-b border-brand-light/40 sticky top-0 z-20">
        <div className="px-6 md:px-10">
          <div className="max-w-7xl mx-auto flex items-center justify-between h-14">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-light">
                  <i className="ri-settings-3-line text-sm text-brand-deep" />
                </div>
                <span className="text-sm font-semibold text-brand-deep">内容管理平台</span>
              </div>

              <div className="flex items-center bg-brand-light rounded-full p-0.5">
                <button
                  onClick={() => setActiveTab('founding')}
                  className={`px-4 py-1.5 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === 'founding'
                      ? 'bg-white text-brand-deep shadow-sm'
                      : 'text-gray-400 hover:text-brand-deep'
                  }`}
                >
                  <i className="ri-vip-crown-line mr-1.5" />
                  创始团队
                </button>
                <button
                  onClick={() => setActiveTab('coreManagement')}
                  className={`px-4 py-1.5 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === 'coreManagement'
                      ? 'bg-white text-brand-deep shadow-sm'
                      : 'text-gray-400 hover:text-brand-deep'
                  }`}
                >
                  <i className="ri-team-line mr-1.5" />
                  核心管理团队
                </button>
                <button
                  onClick={() => setActiveTab('news')}
                  className={`px-4 py-1.5 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === 'news'
                      ? 'bg-white text-brand-deep shadow-sm'
                      : 'text-gray-400 hover:text-brand-deep'
                  }`}
                >
                  <i className="ri-newspaper-line mr-1.5" />
                  资讯管理
                </button>
                <button
                  onClick={() => setActiveTab('config')}
                  className={`px-4 py-1.5 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === 'config'
                      ? 'bg-white text-brand-deep shadow-sm'
                      : 'text-gray-400 hover:text-brand-deep'
                  }`}
                >
                  <i className="ri-settings-3-line mr-1.5" />
                  基础配置
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Export dropdown */}
              <div className="relative">
                <button
                  onClick={() => setExportMenuOpen(!exportMenuOpen)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                  title="导出数据备份"
                >
                  <i className="ri-download-2-line" />
                  导出备份
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {exportMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setExportMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-xl border border-brand-light/40 shadow-lg py-1 min-w-[140px]">
                      <button
                        onClick={handleExportExcel}
                        className="w-full flex items-center gap-2 px-4 py-2 text-xs text-brand-deep hover:bg-brand-light transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-file-excel-2-line text-green-600" />
                        导出 Excel (.xlsx)
                      </button>
                      <button
                        onClick={handleExportJSON}
                        className="w-full flex items-center gap-2 px-4 py-2 text-xs text-brand-deep hover:bg-brand-light transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-code-line text-gray-400" />
                        导出 JSON (.json)
                      </button>
                    </div>
                  </>
                )}
              </div>

              <button
                onClick={handleImportClick}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-400 hover:text-brand-deep hover:bg-brand-light rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                title="导入数据备份"
              >
                <i className="ri-upload-2-line" />
                导入
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="hidden"
              />
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 ml-1 text-xs text-gray-400 hover:text-brand-deep transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-r-line" />
                退出登录
              </button>
            </div>
          </div>
        </div>
      </header>

      {importStatus !== 'idle' && (
        <div className={`px-6 md:px-10 py-2 text-center text-xs font-medium ${
          importStatus === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
        }`}>
          {importMessage}
          <button
            onClick={() => { setImportStatus('idle'); setImportMessage(''); }}
            className="ml-3 underline cursor-pointer"
          >
            关闭
          </button>
        </div>
      )}

      <main className="px-6 md:px-10 py-6" key={refreshKey}>
        <div className="max-w-7xl mx-auto">
          {activeTab === 'founding' ? <FoundingTeamManagement /> : activeTab === 'coreManagement' ? <CoreManagementTeamManagement /> : activeTab === 'news' ? <NewsManagement /> : <SiteConfigManagement />}
        </div>
      </main>
    </div>
  );
}